
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { EXCLUDED_ACTIVITY_FILTERS_KEYS, excludeMatchingFields, getBudgetLabelString, getActivityFilterValues, getInitialHotelFiltersState, handleFilterRemove, isActivityFiltersEmpty } from '@utils';
import { getSelectedCurrency, formatActivityFilterValueLabel, RootState } from '@share/utils';
import { activityFiltersActions } from '@share/store/slices';
import { HomesTypes } from '@share/constants';
import { IAccount } from '@share/common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import './style.scss';

interface IProps {
  isVacationRentals?: boolean | null | undefined;
  isCars?: boolean | null | undefined;

  displayClearAll?: boolean;

  onFiltersOrSortChange: () => void;
}

export function ActivitySelectedFiltersItem(props: IProps) {
  const { isVacationRentals, isCars, displayClearAll } = props;

  const intl = useIntl();
  const guideLanguageDisplayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([intl.locale], { type: 'language' });
    } catch {
      return null;
    }
  }, [intl.locale]);

  const activityFiltersStore = useSelector((state: RootState) => state.activityFiltersStore);
  const activityStore = useSelector((state: RootState) => state.activityStore);

  const account = useSelector((state: RootState) => state.loginStore.account);

  const { loadingFilter: loadingFilterHotels, filterCounters, counters } = activityStore;
  const categoryNodes = filterCounters?.categories ?? counters?.categories;


  const { searchHomes } = activityFiltersStore;

  const hotelInitialFilterState = getInitialHotelFiltersState(account as IAccount, isVacationRentals);
  const isFiltersAppliedHotels = !isActivityFiltersEmpty(activityFiltersStore, (account as IAccount)?.isCapitalVacations);


  const loadingFilter = loadingFilterHotels;
  const isFiltersApplied = isFiltersAppliedHotels;

  const hotelFilterNotExcluded = excludeMatchingFields(activityFiltersStore, hotelInitialFilterState, EXCLUDED_ACTIVITY_FILTERS_KEYS);

  const isSearchHomesApplied = isFiltersAppliedHotels && !isCars && !isVacationRentals && searchHomes !== HomesTypes.All && !!hotelFilterNotExcluded?.searchHomes;

  const filterValues = getActivityFilterValues(activityFiltersStore)?.filter((f) => Object.keys(hotelFilterNotExcluded).includes(f.key));

  if (!isFiltersApplied || loadingFilter || (!filterValues.length && (!isSearchHomesApplied && !isCars))) {
    return null;
  }

  const dispatch = useDispatch();

  const currency = getSelectedCurrency(account as IAccount);
  const handleRemove = (key: string, value: string, isArray: boolean) => {
    handleFilterRemove(isCars, activityFiltersStore, dispatch, key, value, isArray);
    props.onFiltersOrSortChange();
  };

  const handleRemoveClearAll = () => {
    dispatch(activityFiltersActions.resetFilters((account as IAccount)?.isCapitalVacations));
    props.onFiltersOrSortChange();
  };

  const handleRemoveSearchHomes = () => {
    dispatch(activityFiltersActions.setSearchHomes(HomesTypes.All));
    props.onFiltersOrSortChange();
  };

  return (
    <div className="activity-selected-filters-header__content">
      {filterValues.map((f) => (
        <div key={`${f.key}-${f.value}`} className="activity-selected-filters-header__filter" onClick={() => handleRemove(f.key, f.value, f.isArray)}>
          <span className="activity-selected-filters-header__filter-value">
            {f.key === 'budget' ?
              getBudgetLabelString(f.value, currency) :
              f.key === 'ratings' ?
                <FormattedMessage id="stars.count" values={{ count: f.value }} /> :
                formatActivityFilterValueLabel(intl, f.key, f.value, {
                  guideLanguageDisplayNames,
                  categoryNodes,
                })}
          </span>
          <FontAwesomeIcon icon={faTimes} />
        </div>))}

      {isSearchHomesApplied && (
        <div className="activity-selected-filters-header__filter" onClick={handleRemoveSearchHomes}>
          <span className="activity-selected-filters-header__filter-value">
            <FormattedMessage id={searchHomes === HomesTypes.VacationRentalOnly ? "houses" : "Hotels"} />
          </span>
          <FontAwesomeIcon icon={faTimes} />
        </div>)}

      {displayClearAll && filterValues.length > 1 && (
        <div className="activity-selected-filters-header__filter clear-all-button" onClick={handleRemoveClearAll}>
          <span className="activity-selected-filters-header__filter-value">
            <FormattedMessage id="clearAll" />
          </span>
        </div>)}
    </div>
  );
}
